import { useCallback, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { Popover } from '@carouselgg/nexus/popover'
import { ArrowRight, Cable, ChevronsUpDown } from '@carouselgg/nexus/icon'
import { useAuthStore } from '@carouselgg/auth/store'
import { TRUST_LEVELS, nextTrustLevelLabel } from '@carouselgg/auth/trust-levels'
import styles from './IdentityMenu.module.css'

/** Game scores shown in the menu before the rest fold into "+N more". */
const MAX_GAMES = 3

/** The score a player needs to register for competitions — the Verified level. */
const REGISTER_MIN = TRUST_LEVELS.find((l) => l.level === 'Verified')?.min ?? 20

const GAME_SHORT_NAMES: Record<string, string> = {
  'league-of-legends': 'LoL',
  valorant: 'VAL',
  'counter-strike': 'CS2',
  'counter-strike-2': 'CS2',
  'rocket-league': 'RL',
  overwatch: 'OW',
  'dota-2': 'Dota 2',
  dota2: 'Dota 2',
  pubg: 'PUBG',
}

interface IdentityMenuProps {
  /** Desktop rail: the block shrinks to the ringed avatar. The menu still opens. */
  collapsed?: boolean
  /** Called when the menu sends the viewer somewhere — closes the phone drawer. */
  onNavigate?: () => void
}

/**
 * The signed-in viewer at the top of the side nav: their avatar inside their
 * trust ring, their name and score. It opens a trust panel — the Carousel
 * score in detail, game scores, the way to raise it, and Connections. Account
 * things (profile, settings, teams, orgs, theme, log out) live in the top
 * bar's user menu, where people look for them. Under 20 the block also says
 * what to do next: link accounts.
 */
export function IdentityMenu({ collapsed, onNavigate }: IdentityMenuProps) {
  const user = useAuthStore((s) => s.user)
  const trustScore = useAuthStore((s) => s.trustScore)
  const gameTrustScores = useAuthStore((s) => s.gameTrustScores)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  const close = useCallback(() => setOpen(false), [])
  const go = useCallback(() => {
    setOpen(false)
    onNavigate?.()
  }, [onNavigate])

  if (!user) return null

  const name = user.gamer_tag || user.username
  const score = trustScore?.score ?? null
  const level = trustScore?.level
  const lagging = score !== null && score < REGISTER_MIN
  const tip = trustScore?.tips?.[0]
  const shownGames = gameTrustScores.slice(0, MAX_GAMES)
  const moreGames = gameTrustScores.length - shownGames.length

  const avatar = user.avatar_url
    ? <img src={user.avatar_url} alt="" className={styles.avatarImg} />
    : name.charAt(0).toUpperCase()

  return (
    <div className={[styles.identity, collapsed && styles.collapsed].filter(Boolean).join(' ')}>
      <button
        ref={anchorRef}
        type="button"
        className={[styles.block, open && styles.blockOpen].filter(Boolean).join(' ')}
        onClick={() => setOpen((o) => !o)}
        aria-label="Your trust score"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? menuId : undefined}
      >
        <TrustGauge score={score ?? 0} size={40}>
          {avatar}
        </TrustGauge>
        <span className={styles.who}>
          <span className={styles.name}>{name}</span>
          {score !== null && (
            <span className={styles.meta}>
              <strong className={styles.score}>{score}</strong>
              {level && <> · {level}</>}
            </span>
          )}
        </span>
        <ChevronsUpDown size={16} className={styles.chevron} aria-hidden="true" />
      </button>

      {lagging && (
        <Link to="/settings/connections" className={styles.prompt} onClick={go}>
          <span className={styles.promptDot} aria-hidden="true" />
          <span className={styles.promptText}>
            <span className={styles.promptTitle}>Link accounts to compete</span>
            <span className={styles.promptSub}>{REGISTER_MIN - score} more to register</span>
          </span>
        </Link>
      )}

      <Popover id={menuId} open={open} onClose={close} anchorRef={anchorRef} label="Trust score" className={styles.menu}>
        <div className={styles.head}>
          {score !== null && (
            <div className={styles.overall}>
              <TrustGauge score={score} size={56} />
              <div className={styles.overallText}>
                <span className={styles.kicker}>Carousel score</span>
                {level && <span className={styles.level}>{level}</span>}
                <span className={styles.kicker}>{nextTrustLevelLabel(score)}</span>
              </div>
            </div>
          )}

          {shownGames.length > 0 && (
            <div className={styles.games}>
              {shownGames.map((game) => (
                <Link
                  key={game.game_id}
                  to={`/trust/${game.game_slug}`}
                  className={styles.game}
                  onClick={go}
                  aria-label={`${game.game_name} score ${game.score.score}`}
                >
                  <TrustGauge
                    score={game.score.score}
                    size={28}
                  />
                  <span className={styles.gameName}>{GAME_SHORT_NAMES[game.game_slug] ?? game.game_name}</span>
                </Link>
              ))}
              {moreGames > 0 && (
                <Link to="/trust" className={styles.moreGames} onClick={go}>+{moreGames} more</Link>
              )}
            </div>
          )}

          <Link to="/trust" className={styles.raise} onClick={go}>
            <span className={styles.raiseText}>
              <span className={styles.raiseTitle}>Check and raise your trust score</span>
              {tip && (
                <span className={styles.kicker}>
                  Next: {tip.label} <strong className={styles.points}>+{tip.points}</strong>
                </span>
              )}
            </span>
            <ArrowRight size={16} className={styles.raiseIcon} aria-hidden="true" />
          </Link>
        </div>

        <div className={styles.group}>
          <Link to="/settings/connections" className={styles.item} onClick={go}>
            <Cable size={16} aria-hidden="true" />Connections
          </Link>
        </div>
      </Popover>
    </div>
  )
}
